import React, { useState, useEffect, useMemo } from 'react';
import PageModal from '../Layout/PageModal';
import { useData } from '../../hooks/useData';
import { useCore } from '../../hooks/useCore';
import { ChevronLeft, FileText, FileSpreadsheet, FileCode, Codesandbox } from 'lucide-react';
import { ReactComponent as X12Logo } from '../../icons/logo_x12.svg';
import { toastConfig, successToastConfig, dangerToastConfig } from '../../utils/data';
import { useNavigate } from 'react-router-dom';
import { useNavigator } from '../../hooks/useNavigator';
import { InputTextArea } from '../Input/InputTextArea';
import { LoaderSpinner } from '../Layout/LoaderSpinner';
import { Mail, ListFilter, ChartBar, CalendarDays, ChartBarStacked, CodeXml } from 'lucide-react';
import IconButton from '../Input/IconButton';
import { addCommas, formatPercent } from '../../utils/tools';
import { getStatusColor, brandColors } from '../../utils/data';
// import { Button } from '@mui/material';
import { Button } from '../Styled/components';
import { MailPlus, FileScan, GitBranchPlus, ClipboardPlus, Send, ChartColumn, Map, Download } from 'lucide-react';
import ClaimEnvelope from './ClaimEnvelope';
import ClaimDetail from './ClaimDetail';
import { StaticDatePicker } from '@mui/lab';
import { ChargingStationRounded } from '@mui/icons-material';
import D3Chart from '../D3/D3Chart';
import { stackedBarData } from '../../utils/chartData';
import { getCptDetails } from '../../utils/codeTools';
import { revenueData } from '../../utils/chartData';
import { cptData } from '../../utils/codeData';
import ActivityCalendar from '../ActivityCalendar';
import USAMap from '../Map/USAMap';
import MapView from '../Map/MapView';
import { useTheme } from '../../context/ThemeContext';
import classNames from 'classnames';
import ConfirmModal from '../Layout/ConfirmModal';
import { client } from '../../services/api';
const PRE_LEADER_MONTHS = 3;

const getClaimSearchValues = (claim) => {
  if (!claim) {
    return [];
  }

  const subscriberName = [
    claim.loops?.subscriber?.namePrefix,
    claim.loops?.subscriber?.firstName,
    claim.loops?.subscriber?.middleName,
    claim.loops?.subscriber?.name,
    claim.loops?.subscriber?.nameSuffix,
  ].filter(Boolean).join(' ');

  const patientName = [
    claim.loops?.patient?.namePrefix,
    claim.loops?.patient?.firstName,
    claim.loops?.patient?.middleName,
    claim.loops?.patient?.name,
    claim.loops?.patient?.nameSuffix,
  ].filter(Boolean).join(' ');

  return [
    subscriberName,
    patientName,
    claim.loops?.subscriber?.payer?.name,
    claim.loops?.billingProvider?.provider?.name,
    claim.claimInfo?.totalAmount != null ? String(claim.claimInfo.totalAmount) : '',
    claim.loops?.header?.submitter?.name,
  ].filter(Boolean);
};
    
   

const FileDetail = ({coreFile = null, calendarActive = false}) => {
   
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState(coreFile);
  const [version, setVersion] = useState("og");
  const [totalClaims, setTotalClaims] = useState(0);
  const [repricedTotalClaims, setRepricedTotalClaims] = useState(0);
  const [repriced, setRepriced] = useState(false);
  const [totalSavings, setTotalSavings] = useState(0);
  const [locationData, setLocationData] = useState([]);
  const [claimLocationData, setClaimLocationData] = useState([]);
  const navigate = useNavigate();
  const { screens, pathname, searchParams } = useNavigator();
  const { getFileBySlug, filesOpen, fileOpenId, processFile, claimOpenId, setClaimOpenId, buildClaims, appliedClaimSearchTerm } = useData();
  const [view, setView] = useState('claims');
  const [showDownloadConfirm, setShowDownloadConfirm] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [codeColorScheme, setCodeColorScheme] = useState([]);
  const [calendarData, setCalendarData] = useState([
    {
      "date": "2024-01-15",
      "count": 1
    }
  ]);
  const { theme } = useTheme();
  const displayedClaims = useMemo(() => {
    const claims = file?.data?.file?.claims || [];
    if (!appliedClaimSearchTerm) {
      return claims.map((claim, index) => ({ claim, originalIndex: index }));
    }

    const normalizedSearch = appliedClaimSearchTerm.toLowerCase();
    return claims
      .map((claim, index) => ({ claim, originalIndex: index }))
      .filter(({ claim }) =>
        getClaimSearchValues(claim).some((value) =>
          value.toLowerCase().includes(normalizedSearch)
        )
      );
  }, [file, appliedClaimSearchTerm]);

  const handleView = (v) => {
    if(v !== view){
      setView(v);
    }else{
      setView(null);
    }
  }

  useEffect(() => {
    if(coreFile?.versions.length > 0){
      setVersion(coreFile?.versions[coreFile.versions.length - 1].slug);
      setFile(coreFile.versions[coreFile.versions.length - 1]);
    } else {
        setFile(coreFile);
        setVersion('og');
    }
    
  }, [coreFile]);

  // Transform claims data for StackedBarChart
  const claimsChartData = useMemo(() => {
    if (!displayedClaims.length) return [];

    let cData = [];
    let claims = displayedClaims.map(({ claim }) => claim);

    for(var i = 0; i < claims.length; i++){
      let claim = claims[i];
      for(var j = 0; j < claim.serviceLines.length; j++){
        let serviceLine = claim.serviceLines[j];
        let codeType = 'Unknown'; // Better default
        
        if(serviceLine.hcpcs && serviceLine.hcpcs.length > 0){
          let hc = serviceLine.modifiers[0] || serviceLine.hcpcs.split(':')[1];
          
          if(hc) {
            let codeTypeFull = getCptDetails(hc);
            
            if(codeTypeFull && codeTypeFull.section) {
              codeType = codeTypeFull.section;
            } else {
              codeType = 'Unknown';
            }
          } else {
            codeType = 'No Code';
          }
        } else {
          codeType = 'No HCPCS';
        }
        
          
        const amount = 100;
        
        cData.push({
          claimId: `Claim ${i + 1}`,
          type: codeType,
          amount: amount
        });
      }
    }

    
    
    // Transform into D3 stacked bar format: grouped by claimId with types as properties
    const groupedByClaim = {};
    
    cData.forEach(item => {
      const claimId = item.claimId;
      const type = item.type;
      const amount = item.amount;
      
      if (!groupedByClaim[claimId]) {
        groupedByClaim[claimId] = { claimId };
      }
      
      // Sum amounts for same claimId + type combination
      if (groupedByClaim[claimId][type]) {
        groupedByClaim[claimId][type] += amount;
      } else {
        groupedByClaim[claimId][type] = amount;
      }
    });
    
    // Convert to array and sort by claim number to maintain order
    const transformedData = Object.values(groupedByClaim).sort((a, b) => {
      const aNum = parseInt(a.claimId.match(/\d+/)[0]);
      const bNum = parseInt(b.claimId.match(/\d+/)[0]);
      return aNum - bNum;
    });
    
    return transformedData;

  }, [displayedClaims]);


  

  // Transform claims data for GroupedBarChart (similar to revenueData format)
  const claimsGroupedData = useMemo(() => {
    if (!displayedClaims.length) return [];

    let cData = [];
    let claims = displayedClaims.map(({ claim }) => claim);

    for(var i = 0; i < claims.length; i++){
      let claim = claims[i];
      for(var j = 0; j < claim.serviceLines.length; j++){
        let serviceLine = claim.serviceLines[j];
        let codeType = 'Unknown'; // Better default
        
        if(serviceLine.hcpcs && serviceLine.hcpcs.length > 0){
          let hc = serviceLine.modifiers[0] || serviceLine.hcpcs.split(':')[1];
          
          if(hc) {
            let codeTypeFull = getCptDetails(hc);
            
            if(codeTypeFull && codeTypeFull.section) {
              codeType = codeTypeFull.section;
            } else {
              codeType = 'Unknown';
            }
          } else {
            codeType = 'No Code';
          }
        } else {
          codeType = 'No HCPCS';
        }
          
        const amount = 100;
        
        cData.push({
          claimId: `Claim ${i + 1}`,
          type: codeType,
          amount: amount
        });
      }
    }

    // Transform into grouped bar format (like revenueData)
    const groupedByClaim = {};
    
    cData.forEach(item => {
      const claimId = item.claimId;
      const type = item.type;
      const amount = item.amount;
      
      if (!groupedByClaim[claimId]) {
        groupedByClaim[claimId] = { name: claimId };
      }
      
      // Sum amounts for same claimId + type combination
      if (groupedByClaim[claimId][type]) {
        groupedByClaim[claimId][type] += amount;
      } else {
        groupedByClaim[claimId][type] = amount;
      }
    });
    
    // Convert to array and sort by claim number
    const groupedData = Object.values(groupedByClaim).sort((a, b) => {
      const aNum = parseInt(a.name.match(/\d+/)[0]);
      const bNum = parseInt(b.name.match(/\d+/)[0]);
      return aNum - bNum;
    });
    
    return groupedData;

  }, [displayedClaims]);

  // Custom color scheme for charge types
 
  // cms geo by State  
  // cms  provider geo lat lon zip state

      
        // all the providers utilized
        //    across all claims
        //    Codes
        //    Charged
        //    paid   

        //    slide to 150% of medicare


  useEffect(() => {
    if(screens.length > 2 && screens[1] === 'file' && !file){
      getFileBySlug(screens[2]);
    }
  }, [screens]);


  useEffect(() => {
    if(claimOpenId){

      let gData = [];
      for(var i = 0; i < locationData.length; i++){
        let ld = locationData[i];
        if(ld.id === claimOpenId){
          gData.push(ld);
        }
      }

      setClaimLocationData(gData);

    }
  }, [claimOpenId]);

  useEffect(() => {

    if(displayedClaims.length){

      let geoData = [];  
      let tc = 0;
      let tcr = 0;
      let ts = 0;
      let repriced = false;
      displayedClaims.forEach(({ claim }) => {
        tc += claim.claimInfo.totalAmount;
        if(claim.pricing?.repricedAmount){
          repriced = true;
          ts += claim.pricing.savingsAmount;
          tcr += claim.pricing.repricedAmount;
        };
        if(claim.loops.billingProvider?.geo !== undefined){
          let gd = {
            lat: claim.loops.billingProvider.geo.lat,
            lng: claim.loops.billingProvider.geo.lng,
            city: claim.loops.billingProvider.address.city,
            state: claim.loops.billingProvider.address.state,
            name: claim.loops.billingProvider.provider.name,
            type: 'provider',
            id: claim.claimInfo.claimId?.length > 0 ? claim.claimInfo.claimId : null,
            // zip: claim.loops.billingProvider.geo.zip
          }
          geoData.push(gd);
        }

        
        if(claim.loops.subscriber?.geo !== undefined){
          let sd = {
            lat: claim.loops.subscriber.geo.lat,
            lng: claim.loops.subscriber.geo.lng,
            city: claim.loops.subscriber.address.city,
            state: claim.loops.subscriber.address.state,
            name: claim.loops.subscriber.firstName + ' ' + claim.loops.subscriber.name,
            type: 'subscriber',
            id: claim.claimInfo.claimId?.length > 0 ? claim.claimInfo.claimId : null,
          }
          geoData.push(sd);
        }
        
        if(claim.loops.patient?.geo !== undefined){
          let pd = {
            lat: claim.loops.patient.geo.lat,
            lng: claim.loops.patient.geo.lng,
            city: claim.loops.patient.address.city,
            state: claim.loops.patient.address.state,
            name: claim.loops.patient.firstName + ' ' + claim.loops.patient.name,
            type: 'patient',
            id: claim.claimInfo.claimId?.length > 0 ? claim.claimInfo.claimId : null,
          }
          geoData.push(pd);
        }
        


      });
            setLocationData(geoData);
      setTotalClaims(tc);
      setTotalSavings(ts);
      setRepricedTotalClaims(tcr);
      setRepriced(repriced);
    } else {
      setLocationData([]);
      setTotalClaims(0);
      setTotalSavings(0);
      setRepricedTotalClaims(0);
      setRepriced(false);
    }

   
      let c = [];
      for(var key in cptData){
        c.push(cptData[key].color);
      }
      setCodeColorScheme(c);
    
  }, [displayedClaims, file]);

  const handleSendClick = () => {
      }

  const getSelectedVersionLabel = () => {
    if (version === 'og') {
      return 'original';
    }
    const selectedVersion = coreFile?.versions?.find((v) => v.slug === version);
    return selectedVersion?.versionName || selectedVersion?.name || selectedVersion?.slug || 'selected version';
  };

  const handleDownloadClick = () => {
    setShowDownloadConfirm(true);
  };

  const handleDownloadCancel = () => {
    if (downloading) {
      return;
    }
    setShowDownloadConfirm(false);
  };

  const handleDownloadConfirm = async () => {
    if (!file?.slug) {
      setShowDownloadConfirm(false);
      return;
    }

    setDownloading(true);

    try {
      const response = await client.getFileBySlug(file.slug);
      const fileFromDb = response?.ok && response?.data ? response.data : file;
      const downloadName = fileFromDb?.name || file.name || 'claim.txt';
      const rawFileContent = fileFromDb?.rawFile ?? '';

      const blob = new Blob([rawFileContent], { type: 'text/plain;charset=utf-8' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = downloadName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Failed to download selected claim version:', error);
    } finally {
      setDownloading(false);
      setShowDownloadConfirm(false);
    }
  };

  const handleReportClick = () => {
      }

  const handleAuditClick = () => {
        processFile(coreFile.slug,'audit');
  }

  const handleRepriceClick = (slug) => {
        processFile(coreFile.slug,'reprice');
  }
  // Helper function to get file icon (same logic as table)
  const getFileIcon = (fileName) => {
    const extension = fileName.split('.').pop()?.toLowerCase();
    switch (extension) {
      case 'xls':
      case 'xlsx':
        return <FileSpreadsheet size={24} color="#217346" />;
      case 'csv':
        return <FileText size={24} color="#FF6B35" />;
      case 'json':
        return <FileCode size={24} color="#F7DF1E" />;
      case 'x12':
        return <X12Logo style={{ width: '24px', height: '24px' }} />;
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
        return <FileText size={24} color="#FF6B35" />;
      default:
        return <FileText size={24} />;
    }
  };

  const handleClaimClick = (claimId) => {
    navigate(`/file/${filesOpen[fileOpenId].slug}/${claimId}`);
      }

  const handleBackClick = () => {
    navigate('/dashboard');
  };

  const handleVersionClick = (slug) => {
    if(slug === 'og'){
      setFile(coreFile);
      setVersion(slug);
    }else{
      for(var i = 0; i < coreFile.versions.length; i++){
        if(coreFile.versions[i].slug === slug){
                    setFile(coreFile.versions[i]);
          setVersion(slug);
                  }
      }
    }
    
  }

  if(!file && fileOpenId){
    return <LoaderSpinner />;
  }

  return (
    
        <div className="file-detail">
            {/* Back button with rollover */}
            
            <div className="file-detail-header">
              <div className="file-detail-header-left">

                <button 
                  onClick={handleBackClick}
                  style={{ 
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: '0.5rem',
                      borderRadius: '6px',
                      marginRight: '0.5rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      color: 'var(--text-primary)',
                      transition: 'background-color 0.2s ease',
                      alignSelf: 'flex-start'
                  }}
                  onMouseEnter={(e) => {
                      e.target.style.backgroundColor = 'var(--bg-hover)';
                  }}
                  onMouseLeave={(e) => {
                      e.target.style.backgroundColor = 'transparent';
                  }}
                  >
                  <ChevronLeft size={24} />
                  
                </button>
                {getFileIcon(file.name)}
                <p style={{ 
                    margin: 0, 
                    // fontSize: '1.5rem', 
                    // fontWeight: '600',
                    // color: 'var(--text-primary)'
                }}>
                    {file.name}
                </p>
              </div>
              <div className="file-detail-header-right">
                
                <IconButton 
                    icon={<Map />}
                    onChange={() => handleView('map')}
                    active={view === 'map'}
                    size={18}
                    
                />
                <IconButton 
                    icon={<ChartColumn />}
                    onChange={() => handleView('chart')}
                    active={view === 'chart'}
                    size={16}
                    
                />
                <IconButton
                    icon={<CodeXml />}
                    onChange={() => handleView('code')}
                    active={view === 'code'}
                    size={16}
                    
                />
                <IconButton 
                    icon={<CalendarDays />}
                    onChange={() => handleView('calendar')}
                    active={view === 'calendar'}
                    size={16}
                    
                />
                    
                
              </div>
            </div>

            <div className={`file-detail-body ${calendarActive ? 'calendar-active' : ''}`}>
              
              <div className="file-detail-body-left">

                { claimOpenId ? (
                  <ClaimDetail file={file} claimId={claimOpenId} />
                ) : (
                  <>
                  <div className="file-detail-header-info">
                    <p className="label">
                      <Mail size={14} color={brandColors.blue} />&nbsp;
                      <b>{file.data.file.functionalGroup.senderCode} <span className="mute">{`>`}</span> {file.data.file.functionalGroup.receiverCode}</b>
                      {file.data.file.envelope.date} <span className="mute">::</span> {file.data.file.envelope.time}
                    
                    
                    </p>

                    
                      

                    <div className="data-item-group">
                    <span style={{marginBottom: '.55rem',marginTop: '.44rem',backgroundColor: getStatusColor(file.status), color: 'var(--text-primary)'}} className="file-status">{file.status || 'pending'}</span>
                      <div className="data-item">
                        <div className="data-label">Total Claims</div>
                        <div className="data-value">{displayedClaims.length}</div>
                      </div>
                      <div className="data-item">
                        <div className="data-label">Total Amount</div>
                        <div className="data-value"><span style={{color: 'var(--text-secondary)',marginRight: '0.22rem'}} className="mute">$</span>{addCommas(totalClaims.toFixed(2))}</div>
                      </div>
                      { repriced ? (
                        <>
                          <div style={{marginTop: '0.5rem'}} className="data-item">
                            <div style={{position: 'absolute', top: 5, left: -11}} className="active-dot green"></div>
                            <div className="data-label">Savings</div>
                            <div className="data-value">{formatPercent((totalSavings / totalClaims * 100).toFixed(2),false)}<span style={{color: 'var(--text-secondary)',marginLeft: '0.44rem',marginRight: '0.22rem'}} className="mute">$</span>{addCommas(totalSavings.toFixed(2))}</div>
                          </div>
                          <div className="data-item">
                            <div className="data-label">Repriced Amount <span className="mute">(RBP)</span></div>
                            <div className="data-value"><span style={{color: 'var(--text-secondary)',marginRight: '0.22rem'}} className="mute">$</span>{addCommas(repricedTotalClaims.toFixed(2))}</div>
                          </div>
                        </>
                      ):null}
                      <div style={{display: 'flex', marginTop: '1rem', gap: '0.5rem'}} className="flex-row">
                        
                        <Button 
                          variant="contained" 
                          color="primary"
                          onClick={handleRepriceClick}
                        >
                          <GitBranchPlus className="nav-icon" style={{marginRight: 11}} size={18} /> Reprice
                        </Button>
                        
                        <Button 
                          variant="contained" 
                          color="primary"
                          onClick={handleAuditClick}
                        >
                          <FileScan className="nav-icon" style={{marginRight: 11}} size={18} /> Audit
                        </Button>

                        <Button 
                          variant="contained" 
                          color="primary"
                          onClick={handleSendClick}
                          disabled={true}
                        >
                          <Send className="nav-icon" style={{marginRight: 11}} size={18} /> Send
                        </Button>

                        <Button 
                          variant="contained" 
                          color="primary"
                          onClick={handleDownloadClick}
                          disabled={downloading}
                        >
                          <Download className="nav-icon" style={{marginRight: 11}} size={18} /> Download
                        </Button>

                      </div>

                      { coreFile?.versions.length ? (
                        <div style={{marginTop: '1rem'}}>
                          <div className="data-label">Versions</div>
                          <div style={{paddingLeft: 0}} className="data-item-group row">
                          <button onClick={() => handleVersionClick('og')} key={"og0"} style={{backgroundColor: getStatusColor('uploaded'), color: 'var(--text-primary)'}} className={classNames("file-status interactive", version === 'og' ? ' active' : '')}>{'original'}</button>
                              { coreFile.versions.map((v,i) => (
                                <button onClick={() => handleVersionClick(v.slug)} key={i} style={{backgroundColor: getStatusColor(v.status), color: 'var(--text-primary)'}} className={classNames("file-status interactive", version === v.slug ? ' active' : '')}>{v.versionName || 'pending'}</button>
                              ))}
                          </div>
                        </div>
                      ):null}
                      
                    </div>

                  
                  </div>

                  { displayedClaims.length > 0 ? (
                    <>
                    
                      {displayedClaims.map(({ claim: envelope, originalIndex }) => (
                        <ClaimEnvelope
                          key={envelope.claimInfo?.claimId || originalIndex}
                          envelope={envelope}
                          onClick={handleClaimClick}
                          claimIndex={originalIndex}
                        />
                      ))}
                    </>
                  ) : (
                    <p style={{ color: 'var(--text-secondary)', marginTop: 16 }}>No claims match the applied filters.</p>
                  )}
                  </>
                )}

              </div>

             

                

                {view === 'code' && (
                  <div className="file-detail-body-right">
                    <InputTextArea
                      title={null}
                      readOnly={true}
                      style={{color: theme == "dark" ? "#39d93d" : "rgb(3 150 6)",margin: 0,borderRadius:0,minHeight: '53vh', backgroundColor: 'var(--bg-panel)', fontSize: '12px' }}
                      value={file.rawFile.replace(/~/g, '\n')}
                    />
                   
                  </div>
                )}

                {/* { view === 'map' && (
                  <div style={{maxHeight: '33vh'}} className="file-detail-body-right">
                    <USAMap
                      locationData={locationData}
                      // loading={loading}
                      specification={{
                        color: '#87cefa',
                        size: 4,
                        hoverSize: 6,
                        fillOpacity: 0.4,
                        hoverFillOpacity: 0.6,
                        strokeOpacity: 0.8,
                        strokeWidth: 1
                      }}
                    />
                  </div>
                )} */}

                { view === 'map' && (
                  <div className="file-detail-body-right">
                    <MapView
                      locationData={claimOpenId ? claimLocationData : locationData}
                      // loading={loading}
                      specification={{
                        color: '#87cefa',
                        size: 4,
                        hoverSize: 6,
                        fillOpacity: 0.4,
                        hoverFillOpacity: 0.6,
                        strokeOpacity: 0.8,
                        strokeWidth: 1
                      }}
                    />
                  </div>
                )}

                

                { view === 'calendar' && (
                  <div className="file-detail-body-right">
                   
                    <ActivityCalendar data={calendarData} precedingMonths={PRE_LEADER_MONTHS} />
           
                  </div>
                )}

                {view === 'chart' && (
                  <div className="file-detail-body-right">
                       <D3Chart
                         type="grouped-bar"
                         size="medium"
                         data={claimsGroupedData}
                         colorScheme={codeColorScheme}
                         panelClass="mint"
                         legend={true}
                         aspect="horizontal"
                         axisX={true}
                         axisY={true}
                         title="Claims by Service Type"
                         animation={false}
                     />
                      {/* <D3Chart
                        type="stacked-bar"
                        size="large"
                        data={claimsChartData}
                        axisX={true}
                        axisY={true}
                        legend={true}
                        title="Claims by Charge Type (Stacked)"
                        margin={{ top: 40, right: 30, bottom: 20, left: 50 }}
                        colorScheme={codeColorScheme}
                        groupAccessor={d => d.claimId}
                        keysAccessor={d => Object.keys(d).filter(key => key !== 'claimId')}
                        valueAccessor={(d, key) => {
                          // d is the grouped data (InternMap), key is the charge type
                          // We need to access the first item in the group
                          const firstItem = d.values().next().value;
                          return firstItem ? (firstItem[key] || 0) : 0;
                        }}
                      /> */}
                    </div>
                  )}

                
             

              {/* <div className="file-detail-body-right">
                <InputTextArea
                      title={null}
                      readOnly={true}
                      style={{minHeight: '53vh', backgroundColor: 'var(--bg-panel)', color: 'var(--text-primary)', fontSize: '12px' }}
                      value={file.rawFile.replace(/~/g, '\n')}
                      // onChange={onTokenListDescChange}
                      // placeholder='e.g. "My list of favorites" #favorites'
                  
                      // onKeyPress={_handleKeyDown}
                      // autoComplete="off"

                      // focus={true}
                  />
              </div> */}
            </div>

            
            

            {/* File name with logo */}
           
                
            
            {/* <div className="flex-column">
                <InputTextArea
                    title={null}
                    readOnly={true}
                    style={{minHeight: '53vh'}}
                    value={file.rawFile.replace(/~/g, '\n')}
                    // onChange={onTokenListDescChange}
                    // placeholder='e.g. "My list of favorites" #favorites'
                 
                    // onKeyPress={_handleKeyDown}
                    // autoComplete="off"

                    // focus={true}
                />
               
                
            </div> */}
            {showDownloadConfirm && (
              <ConfirmModal
                title="Download"
                description={`Are you sure you want to download "${getSelectedVersionLabel()}" version?`}
                cancelLabel="No"
                confirmLabel="Yes"
                confirmColor="primary"
                onClose={handleDownloadCancel}
                onConfirm={handleDownloadConfirm}
              />
            )}
        </div>
    
  );
};

export default FileDetail;  
